import * as Xstate from "@xstate/react";
import classNames from "classnames";
import DOMPurify from "dompurify";
import React, { FC, useContext, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useParams } from "react-router-dom";

import { GlobalContext } from "../../../app/stateMachines/GlobalContext";
import Button from "../../../components/button/Button";
import RichText from "../../../components/richtext/RichText";
import {
  useAddOrganizationNoteMutation,
  useUpdateOrganizationNoteMutation,
} from "../../../slices/OrganizationNotesSlice";
import { useUpdateWorkNoteMutation } from "../../../slices/WorkNotesSlice";
import { IOrganizationContactNote } from "../../../types/types";
import { serverMessage } from "../../../utils/serverMessage";
import { NOTE_SAVE_FAILED } from "./noteSave";

type Props = {
  isActive: boolean;
  onClose: () => void;
  defaultValues?: IOrganizationContactNote;
  isWork?: boolean;
};

type FormData = {
  title: string;
  note: string;
};

export const OrganizationNoteModal: FC<Props> = ({
  isActive,
  onClose,
  defaultValues,
  isWork,
}) => {
  const { userInfoService } = useContext(GlobalContext);
  const [userInfoState] = Xstate.useActor(userInfoService);
  const { userInfoByEmail } = userInfoState.context;
  const { organizationId } = useParams();

  const [
    addOrganizationNote,
    {
      isLoading: isLoadingOrganizationAdd,
      isSuccess: isSuccessOrganizationAdd,
    },
  ] = useAddOrganizationNoteMutation();
  const [
    updateOrganizationNote,
    {
      isLoading: isLoadingOrganizationUpdate,
      isSuccess: isSuccessOrganizationUpdate,
    },
  ] = useUpdateOrganizationNoteMutation();
  const [
    updateWorkNote,
    { isLoading: isLoadingWorkUpdate, isSuccess: isSuccessWorkUpdate },
  ] = useUpdateWorkNoteMutation();

  const { register, handleSubmit, watch, setValue } = useForm<FormData>({
    defaultValues: defaultValues,
  });

  // The server's answer when saving fails (QA batch 14: the window stayed open silently).
  const [saveError, setSaveError] = useState<string | null>(null);

  const onSubmit = async (data: FormData) => {
    if (
      (data.note || data.title) &&
      !isLoadingOrganizationAdd &&
      !isLoadingOrganizationUpdate &&
      !isLoadingWorkUpdate
    ) {
      setSaveError(null);
      // Rows of the client Notes tab (search-notes) carry their id as `noteId`.
      const noteId = defaultValues?.id || defaultValues?.noteId || "";
      try {
        if (defaultValues) {
          if (isWork) {
            await updateWorkNote({
              note: data.note,
              title: data.title,
              createdAt: defaultValues.createdAt,
              createdByUser: defaultValues.createdByUser,
              createdByUserId: defaultValues.createdByUserId,
              id: noteId,
              workId: defaultValues.workId,
              isPinned: defaultValues.isPinned,
              modifiedByUser: defaultValues.modifiedByUser,
              relayId: defaultValues.relayId,
              updatedAt: defaultValues.updatedAt,
            }).unwrap();
          } else
            await updateOrganizationNote({
              ...defaultValues,
              id: noteId,
              note: data.note,
              title: data.title,
            }).unwrap();
        } else {
          await addOrganizationNote({
            title: data.title,
            note: data.note,
            createdByUserRelayId: `${userInfoByEmail?.id}`,
            businessContactId: organizationId || "",
          }).unwrap();
        }
      } catch (error) {
        setSaveError(serverMessage(error, NOTE_SAVE_FAILED));
      }
    }
  };

  useEffect(() => {
    if (
      isSuccessOrganizationAdd ||
      isSuccessOrganizationUpdate ||
      isSuccessWorkUpdate
    ) {
      onClose();
    }
  }, [
    isSuccessOrganizationAdd,
    isSuccessOrganizationUpdate,
    isSuccessWorkUpdate,
  ]);

  return (
    <div
      className={classNames(
        "modal !z-[64]",
        isActive ? "modal-open" : "modal-close",
      )}>
      <div className={"modal-box p-0"}>
        <div
          className={
            "border-b border-gray-300 px-5 py-4 text-center text-lg font-bold"
          }>
          {defaultValues ? "Update" : "Add"} note
        </div>
        <form
          onSubmit={handleSubmit((data) => {
            data.note = DOMPurify.sanitize(data.note);
            onSubmit(data);
          })}
          className={"px-5 py-4"}>
          <div className={"mb-4"}>
            <input
              type={"text"}
              {...register("title", {})}
              className={"input input-bordered w-full"}
              placeholder={"Title"}
            />
          </div>
          <div>
            <RichText
              value={watch("note") || ""}
              onChange={(val: string) => setValue("note", val)}
              outerHeight={200}
              className={"w-full"}
            />
          </div>
          {saveError && (
            <div role={"alert"} className={"mt-3 text-sm text-red-600"}>
              {saveError}
            </div>
          )}
          <div className={"mt-4 flex justify-end gap-4"}>
            <Button
              label={defaultValues ? "Update" : "Add"}
              buttonType={"submit"}
              disabled={isLoadingOrganizationAdd || isLoadingOrganizationUpdate}
            />
            <Button label={"Cancel"} onClick={onClose} colorType={"outline"} />
          </div>
        </form>
      </div>
    </div>
  );
};
